import React, { useState, type JSX } from "react";
import { useSearchParams } from "react-router";
import { selectProjectInitialized } from "./ProjectSlice_Store";
import { uploadDraftToProject } from "./services/draftUploadService";
import { useAppSelector } from "./hooks/reduxHooks";
import { getCurrentUserId } from "./utils/currentUser";
import StatusBanner, { ButtonSpinner } from "./components/StatusBanner";

const TEXTAREA_PLACEHOLDER = `# My Draft Document

Write your markdown content here...

You can use:
- Lists
- **Bold text**
- *Italic text*
- [Links](https://example.com)
- And more!`;

/** Progress messages for the stages the draft upload service reports. */
const DRAFT_STATUS_TEXT: Record<string, string> = {
  fetching_sas: "Getting upload URL...",
  uploading: "Uploading draft...",
  completed: "Upload complete!",
};

/** What's missing before a draft can be uploaded, or null if nothing is. */
function draftBlocker(content: string, filename: string, projectId: string): string | null {
  if (!content.trim()) {
    return "Please enter some content";
  }
  if (!filename.trim()) {
    return "Please enter a filename";
  }
  if (!projectId.trim()) {
    return "Please enter a project ID";
  }
  return null;
}

/** Shown until the project this draft will be attached to exists. */
function ProjectInitializingBanner(): JSX.Element {
  return (
    <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
      <div className="flex">
        <div className="flex-shrink-0">
          <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
        </div>
        <div className="ml-3">
          <p className="text-sm text-yellow-700">
            Waiting for project to initialize...
          </p>
        </div>
      </div>
    </div>
  );
}

/** A labelled single-line text input with an optional hint underneath. */
function LabeledInput({
  id,
  label,
  value,
  onChange,
  placeholder,
  disabled,
  hint,
  mono = false,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  disabled: boolean;
  hint?: string;
  mono?: boolean;
}): JSX.Element {
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-2">
        {label}
      </label>
      <input
        type="text"
        id={id}
        value={value}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        className={`w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed${
          mono ? " font-mono text-sm" : ""
        }`}
      />
      {hint && <p className="mt-1 text-xs text-gray-500">{hint}</p>}
    </div>
  );
}

/** The markdown body of the draft. */
function ContentTextArea({
  value,
  onChange,
  disabled,
}: {
  value: string;
  onChange: (value: string) => void;
  disabled: boolean;
}): JSX.Element {
  return (
    <div>
      <label htmlFor="content" className="block text-sm font-medium text-gray-700 mb-2">
        Markdown Content
      </label>
      <textarea
        id="content"
        value={value}
        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => onChange(e.target.value)}
        placeholder={TEXTAREA_PLACEHOLDER}
        disabled={disabled}
        rows={20}
        className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm disabled:bg-gray-100 disabled:cursor-not-allowed resize-y"
      />
    </div>
  );
}

function DraftEditorHeader(): JSX.Element {
  return (
    <div>
      <h2 className="text-3xl font-extrabold text-gray-900">
        Simulate adding a generated doc to the project
      </h2>
      <p className="mt-2 text-sm text-gray-600">
        Create markdown drafts that will be automatically added to your project context
      </p>
    </div>
  );
}

function UploadDraftButton({
  uploading,
  disabled,
  onClick,
}: {
  uploading: boolean;
  disabled: boolean;
  onClick: () => Promise<void>;
}): JSX.Element {
  return (
    <button
      onClick={() => void onClick()}
      disabled={disabled}
      className="w-full py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-cvc-blue hover:bg-cvc-blue/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cvc-blue disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
    >
      {uploading ? (
        <span className="flex items-center justify-center">
          <ButtonSpinner />
          Uploading Draft...
        </span>
      ) : (
        "Upload Draft to Project"
      )}
    </button>
  );
}

/** The manual project ID field, seeded from the URL then from Redux. */
function useProjectIdField(): [string, (value: string) => void] {
  const [searchParams] = useSearchParams();
  // Get current project ID from Redux (will be used as default)
  const { currentProjectId } = useAppSelector(state => state.projectSlice);
  const [projectId, setProjectId] = useState<string>("");

  React.useEffect(() => {
    // Priority: 1) URL param, 2) Redux state
    const urlProjectId = searchParams.get('projectId');
    if (urlProjectId && !projectId) {
      setProjectId(urlProjectId);
    } else if (currentProjectId && !projectId) {
      setProjectId(currentProjectId);
    }
  }, [currentProjectId, projectId, searchParams]);

  return [projectId, setProjectId];
}

/** Draft fields plus the upload request, with its progress and error state. */
function useDraftEditorState() {
  const [content, setContent] = useState<string>("");
  const [filename, setFilename] = useState<string>("");
  const [uploading, setUploading] = useState<boolean>(false);
  const [uploadStatus, setUploadStatus] = useState<string>("");
  const [error, setError] = useState<string>("");

  const projectInitialized = useAppSelector(selectProjectInitialized);

  // Get current user info from Redux auth state
  const reduxAuth = useAppSelector(state => state.userAuthAndAdminSlice);

  const [projectId, setProjectId] = useProjectIdField();

  /** Editing any field clears the previous attempt's messages. */
  const editField = (setter: (value: string) => void) => (value: string) => {
    setter(value);
    setError("");
    setUploadStatus("");
  };

  const handleUpload = async () => {
    const blocker = draftBlocker(content, filename, projectId);
    if (blocker) {
      setError(blocker);
      return;
    }

    setUploading(true);
    setError("");
    setUploadStatus("Uploading draft...");

    try {
      const { filePath } = await uploadDraftToProject({
        content,
        filename,
        projectId: projectId.trim(),
        username: getCurrentUserId(reduxAuth),
        onStatusChange: (status: string) => {
          const text = DRAFT_STATUS_TEXT[status];
          if (text) setUploadStatus(text);
        }
      });

      setUploadStatus(`Draft uploaded successfully to ${filePath}! The draft will be added to your project context shortly.`);

      // Clear form after successful upload
      setContent("");
      setFilename("");

    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Upload failed');
      setUploadStatus("");
    } finally {
      setUploading(false);
    }
  };

  return {
    content,
    filename,
    projectId,
    uploading,
    uploadStatus,
    error,
    projectInitialized,
    setContent: editField(setContent),
    setFilename: editField(setFilename),
    setProjectId: editField(setProjectId),
    handleUpload,
  };
}

export default function DraftEditor_Component(): JSX.Element {
  const {
    content,
    filename,
    projectId,
    uploading,
    uploadStatus,
    error,
    projectInitialized,
    setContent,
    setFilename,
    setProjectId,
    handleUpload,
  } = useDraftEditorState();

  return (
    <div className="min-h-screen bg-gray-50 flex items-start justify-center py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl w-full space-y-6">
        <DraftEditorHeader />

        {!projectInitialized && <ProjectInitializingBanner />}

        <div className="bg-white shadow rounded-lg p-6 space-y-6">
          <LabeledInput
            id="projectId"
            label="Project ID (paste from console logs)"
            value={projectId}
            onChange={setProjectId}
            placeholder="e.g., e7be688a-c83b-4e77-897e-ae0c7249acb0"
            disabled={uploading}
            hint="Copy the project ID from your chat window's URL (e.g., ?projectId=xxx) or console logs"
            mono
          />

          <LabeledInput
            id="filename"
            label="Filename (will be saved as .md)"
            value={filename}
            onChange={setFilename}
            placeholder="my-draft-document"
            disabled={uploading}
          />

          <ContentTextArea value={content} onChange={setContent} disabled={uploading} />

          {/* Character Count */}
          <div className="text-sm text-gray-500 text-right">
            {content.length} characters
          </div>

          <UploadDraftButton
            uploading={uploading}
            disabled={uploading || !content.trim() || !filename.trim() || !projectId.trim()}
            onClick={handleUpload}
          />

          {error && <StatusBanner tone="error" message={error} />}
          {uploadStatus && !error && <StatusBanner tone="success" message={uploadStatus} />}
        </div>
      </div>
    </div>
  );
}
